/* eslint-disable no-console */
import mixpanel from 'mixpanel-browser';
import React, { createContext, ReactNode, useContext } from 'react';
import {
  AnalyticsConfig,
  AnalyticsEvent,
  PageViewEvent,
  UserIdentity
} from '../types';
import { GeneralEvents } from '../types/events';
import { sanitizeUserProperties, shouldInitializeAnalytics } from '../utils';

let mixpanelInstance: typeof mixpanel | null = null;

const mapUserToMixpanelProfile = (user: UserIdentity): Record<string, any> => ({
  ...(user.username && { $name: user.username }),
  ...(user.email && { $email: user.email.toLowerCase() }),
  ...(user.id && { user_id: user.id }),
  ...(user.roles?.length && { roles: user.roles }),
  ...(user.products?.length && { products: user.products }),
  ...(user.features?.length && { features: user.features }),
  ...(user.planId && { plan_id: user.planId }),
  ...(user.subscriptionStatus && {
    subscription_status: user.subscriptionStatus
  })
});

export const initMixpanel = (
  config: AnalyticsConfig,
  user?: UserIdentity
): typeof mixpanel => {
  if (mixpanelInstance) {
    return mixpanelInstance;
  }

  const shouldInit =
    shouldInitializeAnalytics(user) && config.mixpanel?.enabled !== false;

  if (shouldInit && config.mixpanel?.token) {
    try {
      mixpanel.init(config.mixpanel.token, {
        debug: config.mixpanel.debug || false,
        track_pageview: false, // We'll handle this manually
        persistence: 'localStorage',
        api_host: config.mixpanel.apiHost || 'https://api.mixpanel.com',
        autocapture: {
          pageview: 'full-url',
          click: true,
          input: true,
          scroll: true,
          submit: true,
          capture_text_content: true
        },
        record_sessions_percent: config.mixpanel.sessionReplayPercent ?? 10
      });
      mixpanelInstance = mixpanel;
    } catch (error) {
      console.warn('Failed to initialize Mixpanel:', error);
    }
  }

  return mixpanelInstance || mixpanel;
};

export const identifyUser = (user: UserIdentity): void => {
  if (!mixpanelInstance || !user.username) return;

  try {
    const properties = sanitizeUserProperties(user);
    mixpanelInstance.identify(user.username.toLowerCase());
    mixpanelInstance.people.set(properties);
  } catch (error) {
    console.warn('Mixpanel identify error:', error);
  }
};

export const setUser = (user: UserIdentity): void => {
  if (!mixpanelInstance || !user.username) return;

  try {
    const properties = mapUserToMixpanelProfile(user);
    mixpanelInstance.people.set(properties);
  } catch (error) {
    console.warn('Mixpanel set user error:', error);
  }
};

export const trackEvent = (event: AnalyticsEvent): void => {
  if (!mixpanelInstance) return;

  try {
    mixpanelInstance.track(event.name, event.properties);
  } catch (error) {
    console.warn('Mixpanel track error:', error);
  }
};

export const trackPageView = (event: PageViewEvent): void => {
  if (!mixpanelInstance) return;

  try {
    mixpanelInstance.track(GeneralEvents.PAGE_VISIT, {
      page: event.page,
      path: event.path || window.location.pathname,
      title: event.title,
      ...event.properties
    });
  } catch (error) {
    console.warn('Mixpanel page view error:', error);
  }
};

export const resetMixpanel = (): void => {
  if (mixpanelInstance) {
    mixpanelInstance.reset();
    mixpanelInstance = null;
  }
};

// React Context
interface MixpanelContextType {
  mixpanel: typeof mixpanel | null;
  identify: (user: UserIdentity) => void;
  track: (event: AnalyticsEvent) => void;
  trackPageView: (event: PageViewEvent) => void;
  setUser: (user: UserIdentity) => void;
}

const MixpanelContext = createContext<MixpanelContextType>({
  mixpanel: null,
  identify: () => {},
  track: () => {},
  trackPageView: () => {},
  setUser: () => {}
});

interface MixpanelProviderProps {
  children: ReactNode;
  config: AnalyticsConfig;
  user?: UserIdentity;
}

export const MixpanelProvider: React.FC<MixpanelProviderProps> = ({
  children,
  config,
  user
}) => {
  const instance = initMixpanel(config, user);

  const contextValue: MixpanelContextType = {
    mixpanel: instance,
    identify: identifyUser,
    track: trackEvent,
    trackPageView,
    setUser
  };

  return (
    <MixpanelContext.Provider value={contextValue}>
      {children}
    </MixpanelContext.Provider>
  );
};

export const useMixpanel = (): MixpanelContextType => {
  return useContext(MixpanelContext);
};

export const getMixpanelInitialized = (): boolean => {
  return (
    mixpanelInstance !== null ||
    (typeof window !== 'undefined' && (window as any).mixpanel)
  );
};

export { mixpanel };
export type { MixpanelContextType };
